import { useState, useEffect, useMemo, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
// Tables + QR links backed by Supabase (restaurant_tables)
import {
  fetchTables,
  addTable,
  updateTable,
  deleteTable,
  type RestaurantTable,
} from '@/lib/data';
import { useNavigate } from 'react-router-dom';
import { useRestaurant, useAccess } from '@/lib/session';
import { hasFeature, requiredTierFor, PLAN_LABEL } from '@/lib/access';

interface TableSection {
  id: string;
  name: string;
  tables: RestaurantTable[];
}

const UNZONED = 'Unassigned';

function tableName(table: RestaurantTable): string {
  return table.label ?? table.id;
}

export default function QrPage() {
  const restaurant = useRestaurant();
  const access = useAccess();
  const navigate = useNavigate();
  // QR / scan table ordering is a Growth+ feature. The page stays usable for
  // managing tables, but online ordering via these QR codes only works on
  // Growth+ — show an upgrade prompt for sub-Growth plans.
  const qrOrderingUnlocked = hasFeature(access.effectiveTier, 'qr-ordering');
  const qrTier = requiredTierFor('qr-ordering') ?? 'growth';

  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [activeSection, setActiveSection] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showDisabled, setShowDisabled] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Add Table modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLabel, setAddLabel] = useState('');
  const [addSeats, setAddSeats] = useState('');
  const [addZone, setAddZone] = useState('');

  // Edit Table modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editSeats, setEditSeats] = useState('');
  const [editZone, setEditZone] = useState('');
  const [editOrdering, setEditOrdering] = useState(true);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<RestaurantTable | null>(null);

  const [busy, setBusy] = useState(false);

  // Toast
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchTables(restaurant.id);
      setTables(rows);
      const zones = [...new Set(rows.map((t) => t.zone ?? UNZONED))];
      setExpandedSections(new Set(zones));
      setActiveSection((prev) => (prev && zones.includes(prev) ? prev : zones[0] ?? ''));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load tables.');
    } finally {
      setLoading(false);
    }
  }, [restaurant.id]);

  useEffect(() => { void load(); }, [load]);

  // Group real tables into sections by zone (client-side)
  const sections = useMemo<TableSection[]>(() => {
    const map = new Map<string, RestaurantTable[]>();
    for (const t of tables) {
      const zone = t.zone ?? UNZONED;
      const list = map.get(zone);
      if (list) list.push(t); else map.set(zone, [t]);
    }
    return [...map.entries()].map(([zone, ts]) => ({ id: zone, name: zone, tables: ts }));
  }, [tables]);

  const allTables = tables;
  const enabledCount = allTables.filter((t) => t.orderingEnabled).length;
  const occupiedCount = allTables.filter((t) => t.status === 'occupied').length;

  const currentSection = sections.find((s) => s.id === activeSection) ?? sections[0];
  const displayedTables = currentSection
    ? currentSection.tables.filter((t) => showDisabled || t.orderingEnabled)
    : [];

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fullUrlFor = (table: RestaurantTable) =>
    table.qrUrl ? `${window.location.origin}${table.qrUrl}` : '';

  const copyLink = async (table: RestaurantTable) => {
    const fullUrl = fullUrlFor(table);
    if (!fullUrl) return;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedId(table.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setCopiedId(table.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const toggleOrdering = async (table: RestaurantTable) => {
    const next = !table.orderingEnabled;
    try {
      await updateTable(restaurant.id, table.id, { orderingEnabled: next });
      setTables((prev) => prev.map((t) => (t.id === table.id ? { ...t, orderingEnabled: next } : t)));
      showToast(`${tableName(table)} ordering ${next ? 'enabled' : 'disabled'}`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to update the table.');
    }
  };

  const openAddModal = () => {
    setAddLabel('');
    setAddSeats('');
    setAddZone(currentSection && currentSection.id !== UNZONED ? currentSection.id : '');
    setAddModalOpen(true);
  };

  const handleAddTable = async () => {
    if (!addLabel.trim() || busy) return;
    setBusy(true);
    try {
      const created = await addTable(restaurant.id, restaurant.slug, {
        label: addLabel.trim(),
        seats: Number(addSeats) || 4,
        zone: addZone.trim() || undefined,
      });
      setTables((prev) => [...prev, created]);
      const zone = created.zone ?? UNZONED;
      setExpandedSections((prev) => new Set(prev).add(zone));
      setActiveSection(zone);
      setAddModalOpen(false);
      showToast(`${tableName(created)} added`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to add the table.');
    } finally {
      setBusy(false);
    }
  };

  const openEditModal = (table: RestaurantTable) => {
    setEditingTable(table);
    setEditLabel(table.label ?? '');
    setEditSeats(String(table.seats));
    setEditZone(table.zone ?? '');
    setEditOrdering(table.orderingEnabled);
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingTable || !editLabel.trim() || busy) return;
    const patch = {
      label: editLabel.trim(),
      seats: Number(editSeats) || 2,
      zone: editZone.trim() || null,
      orderingEnabled: editOrdering,
    };
    setBusy(true);
    try {
      await updateTable(restaurant.id, editingTable.id, patch);
      setTables((prev) => prev.map((t) => (t.id === editingTable.id ? { ...t, ...patch } : t)));
      const zone = patch.zone ?? UNZONED;
      setExpandedSections((prev) => new Set(prev).add(zone));
      setActiveSection(zone);
      setEditModalOpen(false);
      showToast(`${patch.label} updated`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to update the table.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (table: RestaurantTable) => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteTable(restaurant.id, table.id);
      setTables((prev) => prev.filter((t) => t.id !== table.id));
      setDeleteTarget(null);
      showToast(`${tableName(table)} deleted`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to delete the table.');
    } finally {
      setBusy(false);
    }
  };

  const regenerateLink = async (table: RestaurantTable) => {
    const qrUrl = `/t/${restaurant.slug}/${table.id}`;
    try {
      await updateTable(restaurant.id, table.id, { qrUrl });
      setTables((prev) => prev.map((t) => (t.id === table.id ? { ...t, qrUrl } : t)));
      showToast(`${tableName(table)} QR regenerated`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to regenerate the link.');
    }
  };

  const handlePrintSheet = () => {
    window.print();
  };

  const handleGenerateAll = async () => {
    if (busy || tables.length === 0) return;
    setBusy(true);
    try {
      await Promise.all(
        tables.map((t) => updateTable(restaurant.id, t.id, { qrUrl: `/t/${restaurant.slug}/${t.id}` })),
      );
      setTables((prev) => prev.map((t) => ({ ...t, qrUrl: `/t/${restaurant.slug}/${t.id}` })));
      showToast('All QR codes regenerated');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to regenerate QR codes.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Left sidebar: sections & tables */}
      <div className="w-64 lg:w-72 border-r border-background-200 bg-background-50 overflow-y-auto shrink-0">
        <div className="p-4 border-b border-background-200">
          <h3 className="text-sm font-semibold text-foreground-800 mb-2">Sections</h3>
          <div className="flex items-center gap-1">
            <div className="flex-1 bg-background-100 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-foreground-800">{allTables.length}</p>
              <p className="text-xs text-foreground-500">Tables</p>
            </div>
            <div className="flex-1 bg-background-100 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-foreground-800">{enabledCount}</p>
              <p className="text-xs text-foreground-500">Active</p>
            </div>
            <div className="flex-1 bg-background-100 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-foreground-800">{occupiedCount}</p>
              <p className="text-xs text-foreground-500">Occupied</p>
            </div>
          </div>
        </div>

        <div className="p-3 space-y-1">
          {sections.map((section) => (
            <div key={section.id}>
              <button
                onClick={() => toggleSection(section.id)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                  activeSection === section.id
                    ? 'bg-primary-100/70 text-primary-700 font-medium'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${expandedSections.has(section.id) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-sm`}></i>
                  </div>
                  <span>{section.name}</span>
                </div>
                <span className="text-xs text-foreground-400">{section.tables.length}</span>
              </button>

              {expandedSections.has(section.id) && (
                <div className="ml-6 mt-0.5 space-y-0.5">
                  {section.tables.map((table) => (
                    <button
                      key={table.id}
                      onClick={() => { setActiveSection(section.id); }}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        activeSection === section.id
                          ? 'text-foreground-700'
                          : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${table.orderingEnabled ? 'bg-green-500' : 'bg-foreground-300'}`}></span>
                      <span>{tableName(table)}</span>
                      <span className="ml-auto text-foreground-400">{table.seats}s</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main content: QR card grid */}
      <div className="flex-1 overflow-y-auto">
        {/* Toast */}
        {toast && (
          <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-2 shadow-sm animate-[slideInRight_0.25s_ease-out]">
            <i className="ri-checkbox-circle-line text-green-500"></i>
            {toast}
          </div>
        )}

        {/* Top actions bar */}
        <div className="sticky top-0 z-20 bg-background-50 border-b border-background-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground-800">
              {currentSection?.name || 'All Tables'}
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {displayedTables.length} tables · {displayedTables.filter((t) => t.orderingEnabled).length} active
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
                className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer"
              />
              Show disabled
            </label>
            <button
              onClick={() => void handleGenerateAll()}
              className="px-3 py-1.5 text-xs font-medium bg-accent-500 text-background-50 rounded-md hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-qr-code-line mr-1"></i>Generate All
            </button>
            <button
              onClick={handlePrintSheet}
              className="px-3 py-1.5 text-xs font-medium border border-background-300 rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-printer-line mr-1"></i>Print Sheet
            </button>
            <button
              onClick={openAddModal}
              className="px-3 py-1.5 text-xs font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>Add Table
            </button>
          </div>
        </div>

        {/* Plan gate: QR online ordering needs Growth+ */}
        {!qrOrderingUnlocked && (
          <div className="m-5 flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-accent-50 border border-accent-200">
            <div className="flex items-start gap-2 text-sm text-accent-700">
              <i className="ri-lock-2-line mt-0.5"></i>
              <p>
                Guests can view your digital menu from these QR codes, but
                <span className="font-medium"> online ordering is part of the {PLAN_LABEL[qrTier]} plan</span>.
                Upgrade to let guests scan &amp; order from the table.
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard/billing')}
              className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Upgrade to {PLAN_LABEL[qrTier]}
            </button>
          </div>
        )}

        {/* Load error */}
        {loadError && (
          <div className="m-5 flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-700 flex items-center gap-2"><i className="ri-error-warning-line"></i>{loadError}</p>
            <button onClick={() => void load()} className="text-sm font-medium text-red-700 hover:text-red-800 underline cursor-pointer whitespace-nowrap">Retry</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="py-16 text-center">
            <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
            <p className="text-sm text-foreground-500 mt-2">Loading tables...</p>
          </div>
        )}

        {/* Cards grid */}
        {!loading && (
          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {displayedTables.map((table) => (
                <QrTableCard
                  key={table.id}
                  table={table}
                  copied={copiedId === table.id}
                  fullUrl={fullUrlFor(table)}
                  onCopy={() => void copyLink(table)}
                  onToggle={() => void toggleOrdering(table)}
                  onEdit={() => openEditModal(table)}
                  onGenerate={() => void regenerateLink(table)}
                  onDelete={() => setDeleteTarget(table)}
                />
              ))}
            </div>

            {!loadError && displayedTables.length === 0 && (
              <div className="py-16 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-qr-code-line text-xl text-foreground-300"></i>
                </div>
                {tables.length === 0 ? (
                  <>
                    <p className="text-sm text-foreground-500">No tables yet — add your first table</p>
                    <button
                      onClick={openAddModal}
                      className="mt-3 px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line mr-1"></i>Add Table
                    </button>
                  </>
                ) : (
                  <p className="text-sm text-foreground-500">No tables in this section</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Table Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setAddModalOpen(false)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out]">
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <h3 className="font-semibold text-foreground-800">Add Table</h3>
              <button onClick={() => setAddModalOpen(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Table Label</label>
                <input type="text" value={addLabel} onChange={(e) => setAddLabel(e.target.value)} placeholder="e.g. Table 9" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Seats</label>
                <input type="number" value={addSeats} onChange={(e) => setAddSeats(e.target.value)} placeholder="4" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Zone / Section</label>
                <input type="text" value={addZone} onChange={(e) => setAddZone(e.target.value)} list="qr-zone-options" placeholder="e.g. Main Hall" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
                <datalist id="qr-zone-options">
                  {sections.filter((s) => s.id !== UNZONED).map((s) => (<option key={s.id} value={s.id} />))}
                </datalist>
              </div>
              <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                <button onClick={() => void handleAddTable()} disabled={busy || !addLabel.trim()} className="px-5 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">{busy ? 'Adding...' : 'Add Table'}</button>
                <button onClick={() => setAddModalOpen(false)} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Table Modal */}
      {editModalOpen && editingTable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setEditModalOpen(false)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out]">
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <h3 className="font-semibold text-foreground-800">Edit Table</h3>
              <button onClick={() => setEditModalOpen(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Table Label</label>
                <input type="text" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Seats</label>
                <input type="number" value={editSeats} onChange={(e) => setEditSeats(e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Zone / Section</label>
                <input type="text" value={editZone} onChange={(e) => setEditZone(e.target.value)} list="qr-zone-options-edit" placeholder="e.g. Main Hall" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
                <datalist id="qr-zone-options-edit">
                  {sections.filter((s) => s.id !== UNZONED).map((s) => (<option key={s.id} value={s.id} />))}
                </datalist>
              </div>
              <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                <input type="checkbox" checked={editOrdering} onChange={(e) => setEditOrdering(e.target.checked)} className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer" />
                Ordering enabled
              </label>
              <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                <button onClick={() => void handleEditSave()} disabled={busy || !editLabel.trim()} className="px-5 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">{busy ? 'Saving...' : 'Save'}</button>
                <button onClick={() => setEditModalOpen(false)} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl shadow-lg mx-4 p-5 animate-[slideInUp_0.15s_ease-out]">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <h4 className="text-center font-semibold text-foreground-800 mb-1">Delete Table?</h4>
            <p className="text-center text-sm text-foreground-500 mb-4">
              "{tableName(deleteTarget)}" and its QR link will be permanently removed.
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => void handleDelete(deleteTarget)} disabled={busy} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-background-50 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">{busy ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QrTableCard({ table, copied, fullUrl, onCopy, onToggle, onEdit, onGenerate, onDelete }: {
  table: RestaurantTable;
  copied: boolean;
  fullUrl: string;
  onCopy: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onGenerate: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const name = tableName(table);

  return (
    <div className={`bg-background-50 border rounded-lg p-4 transition-all ${
      table.orderingEnabled ? 'border-background-200 hover:border-background-300' : 'border-background-200 opacity-60'
    }`}>
      {/* Top row: table name + actions */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground-800">{name}</span>
            <span className={`w-1.5 h-1.5 rounded-full ${table.orderingEnabled ? 'bg-green-500' : 'bg-foreground-300'}`}></span>
          </div>
          <p className="text-xs text-foreground-500">{table.seats} seats · {table.zone ?? UNZONED}</p>
        </div>
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-more-2-fill text-sm text-foreground-500"></i>
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)}></div>
              <div className="absolute right-0 top-full mt-1 w-44 bg-background-50 border border-background-200 rounded-lg shadow-lg z-30 py-1">
                <button
                  onClick={() => { onEdit(); setMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Edit Table
                </button>
                <button
                  onClick={() => { onGenerate(); setMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Regenerate Link
                </button>
                <button
                  onClick={() => { onToggle(); setMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {table.orderingEnabled ? 'Disable Ordering' : 'Enable Ordering'}
                </button>
                <button
                  onClick={() => { onDelete(); setMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Delete Table
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* QR Code */}
      <div className="flex justify-center mb-3">
        <div className="w-32 h-32 bg-background-100 rounded-lg flex items-center justify-center border border-background-200 overflow-hidden">
          {fullUrl ? (
            // QR generated locally (no external image service / no offsite call).
            <QRCodeSVG
              value={fullUrl}
              size={120}
              bgColor="#f0f8fa"
              fgColor="#03525f"
              level="M"
              title={`QR for ${name}`}
            />
          ) : (
            <i className="ri-qr-code-line text-3xl text-foreground-300"></i>
          )}
        </div>
      </div>

      {/* Link */}
      <div className="flex items-center gap-1 mb-3 px-2 py-1.5 bg-background-100 rounded-md">
        <span className="text-xs text-foreground-500 font-mono truncate flex-1">
          {fullUrl || '—'}
        </span>
        <button
          onClick={onCopy}
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-background-200 transition-colors cursor-pointer"
          title="Copy link"
        >
          <i className={`${copied ? 'ri-check-line text-green-600' : 'ri-file-copy-line text-foreground-500'} text-xs`}></i>
        </button>
      </div>

      {/* Stats */}
      <div className="flex items-center divide-x divide-background-200">
        <div className="flex-1 text-center">
          <p className="text-xs font-semibold text-foreground-800 capitalize">{table.status}</p>
          <p className="text-xs text-foreground-400">Status</p>
        </div>
        <div className="flex-1 text-center">
          <p className="text-xs font-semibold text-foreground-800">
            {table.seatedMinutes != null && table.seatedMinutes > 0 ? `${table.seatedMinutes}m` : '—'}
          </p>
          <p className="text-xs text-foreground-400">Seated</p>
        </div>
        <div className="flex-1 text-center">
          <p className="text-xs font-semibold text-foreground-800">
            {table.amount != null && table.amount > 0 ? `₹${table.amount}` : '—'}
          </p>
          <p className="text-xs text-foreground-400">Amount</p>
        </div>
      </div>
    </div>
  );
}
